import { ref } from 'vue';

export interface ConfirmRequest {
  title: string;
  /** 正文；换行会原样保留，可以列出「将要发生什么」 */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 危险操作用红色主按钮：删除、解除关联、整段停用 */
  danger?: boolean;
  /** 需要用户填一句话（催办留言、退回意见）时给出提示语 */
  prompt?: string;
  promptPlaceholder?: string;
  promptRequired?: boolean;
  defaultValue?: string;
}

interface PendingConfirm extends ConfirmRequest {
  id: number;
  resolve: (value: string | null) => void;
}

/**
 * 应用内确认框。
 *
 * 替换 window.confirm / window.prompt：浏览器原生框长得和系统一样，
 * 在刚做过视觉统一的界面里格外割裂；而且原生 prompt 连「必填」都做不到，
 * 催办留言这种正经业务输入走在系统灰框里也不合适。
 *
 * 用法与原生一致地简单：`await confirmAction({ title })` 返回 true/false；
 * 带 prompt 时返回输入的字符串（取消为 null）。
 */
const current = ref<PendingConfirm | null>(null);
let seed = 0;

export function useConfirmState() {
  return current;
}

export function requestConfirm(request: ConfirmRequest): Promise<string | null> {
  return new Promise((resolve) => {
    // 同一时刻只留一个：连点两次不该叠出两个确认框
    current.value?.resolve(null);
    seed += 1;
    current.value = { ...request, id: seed, resolve };
  });
}

export function settleConfirm(value: string | null): void {
  const pending = current.value;
  current.value = null;
  pending?.resolve(value);
}

/** 只要「确认/取消」时用这个；返回 true 表示确认 */
export async function confirmAction(request: ConfirmRequest): Promise<boolean> {
  const result = await requestConfirm(request);
  return result !== null;
}

/** 需要用户填一句话时用这个；返回 null 表示取消 */
export function promptAction(request: ConfirmRequest & { prompt: string }): Promise<string | null> {
  return requestConfirm(request);
}
